import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Workflow, Plus, Trash2 } from 'lucide-react'
import {
  getPreparations, createPreparation, deletePreparation,
} from '../api/brainer'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { SearchInput } from '../components/SearchInput'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { BPreparation } from '../types'

const blankForm = { name: '', description: '', scheduled: false, clock: '', eventSubscription: '' }
type Form = typeof blankForm

export function BrainerPreparations() {
  const fmt      = useDateFmt()
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const { data: preps = [], isLoading } = useQuery({ queryKey: ['b-preps'], queryFn: () => getPreparations().then((r) => r.data) })

  const [showCreate, setShowCreate] = useState(false)
  const [cForm, setCForm] = useState<Form>(blankForm)
  const create = useMutation({
    mutationFn: () => createPreparation({ name: cForm.name, description: cForm.description || undefined, scheduled: cForm.scheduled, clock: cForm.clock || undefined, eventSubscription: cForm.eventSubscription || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-preps'] }); setShowCreate(false); setCForm(blankForm) },
  })

  const [deleteTarget, setDeleteTarget] = useState<BPreparation | null>(null)
  const del = useMutation({
    mutationFn: (id: string) => deletePreparation(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-preps'] }); setDeleteTarget(null) },
  })

  const [search, setSearch] = useState('')
  const filtered = preps.filter((p: BPreparation) =>
    [p.name, p.description ?? '', p.eventSubscription ?? ''].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Preparations</h1>
          <p className="text-sm text-ink-dim mt-0.5">Workflows  ordered sequences of steps connected by conditional edges</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search preparations" />
          <Button icon={Plus} onClick={() => setShowCreate(true)}>New preparation</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : preps.length === 0 ? (
          <EmptyState icon={Workflow} title="No preparations" description="Build a workflow by adding and connecting steps" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Trigger</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p: BPreparation) => (
                <tr
                  key={p.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/brainer/preparations/${p.id}`)}
                >
                  <td>
                    <p className="font-medium text-ink">{p.name}</p>
                    {p.description && <p className="text-xs text-ink-dim mt-0.5">{p.description}</p>}
                  </td>
                  <td>
                    {p.scheduled ? (
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">cron: {p.clock}</span>
                    ) : p.eventSubscription ? (
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">event: {p.eventSubscription}</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-paper-sunken text-ink-dim">manual</span>
                    )}
                  </td>
                  <td className="text-ink-dim">{fmt(p.createdAt)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-3 justify-end">
                      <button onClick={() => setDeleteTarget(p)} className="text-ink-dim hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <Modal title="New preparation" onClose={() => setShowCreate(false)}>
          <PrepForm form={cForm} onChange={setCForm} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!cForm.name || create.isPending} loading={create.isPending}>
              Create
            </Button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete preparation"
          message={`Delete "${deleteTarget.name}" and all its journey history?`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}
    </div>
  )
}

function PrepForm({ form, onChange }: { form: Form; onChange: (f: Form) => void }) {
  return (
    <div className="space-y-4">
      <Field label="Name *">
        <input value={form.name} onChange={e => onChange({ ...form, name: e.target.value })} placeholder="e.g. Customer Onboarding" className="field" />
      </Field>
      <Field label="Description">
        <input value={form.description} onChange={e => onChange({ ...form, description: e.target.value })} placeholder="Optional" className="field" />
      </Field>
      <label className="flex items-center gap-2 text-sm text-ink-dim cursor-pointer">
        <input type="checkbox" checked={form.scheduled} onChange={e => onChange({ ...form, scheduled: e.target.checked })} className="rounded" />
        Scheduled (cron)
      </label>
      {form.scheduled && (
        <Field label="Cron expression *">
          <input value={form.clock} onChange={e => onChange({ ...form, clock: e.target.value })} placeholder="0 9 * * *" className="field font-mono" />
        </Field>
      )}
      <Field label="Event subscription">
        <input value={form.eventSubscription} onChange={e => onChange({ ...form, eventSubscription: e.target.value })} placeholder="event.name" className="field" />
      </Field>
    </div>
  )
}
